import { useState, useContext } from "react";
import { reqFunction } from "utils/API";
import ConsumptionChartFilter from "./ConsumptionChartFilter";
import DateInput from "components/fields/DateInput";
import { useQuery } from "@tanstack/react-query";
import MixChart from "components/charts/MixChart";
import { useParams, useSearchParams } from "react-router-dom";
import { OneDeviceObj } from ".";
import { ThemeContext } from "ThemeProvider";
import ApexChart from "components/charts/ApexChart";
import moment from "moment-jalaali";
import { getToday } from "utils/TimeUtiles";
import {
  renderComplexChartData,
  renderComplexChartOptions,
} from "utils/globalUtils";

interface CounterConsumptionChartProps {
  deviceInfo: [OneDeviceObj];
  fromDate: any;
  setFromDate: React.Dispatch<any>;
  tillDate: any;
  setTillDate: React.Dispatch<any>;
  mode?: "dashboard" | "full";
}

const CounterConsumptionChart = (props: CounterConsumptionChartProps) => {
  const AdminPermissions: string[] = JSON.parse(
    window.localStorage.getItem("permissions")
  );

  const { isDark } = useContext(ThemeContext);
  const { deviceSerial } = useParams();
  const { deviceInfo, fromDate, setFromDate, tillDate, setTillDate, mode } =
    props;

  type Tab = "daily" | "cumulative" | "range";
  const [chartType, setChartType] = useState<Tab>("daily"); // default: daily
  const [apexSelection, setApexSelection] = useState<"one_day" | "one_week">(
    "one_day"
  );
  // Move endDate by N days (negative for yesterday, positive for tomorrow)
  const shiftEndDate = (days: number) => {
    setEndDate((prev: any) => prev.clone().add(days, "day"));
  };

  const [apexChartType, setApexChartType] = useState<"bar" | "line">("bar");

  // Start & End Dates for ApexChart
  const [endDate, setEndDate] = useState(getToday());
  const [startDate, setStartDate] = useState(endDate);

  const [searchParams, setSearchParams] = useSearchParams();

  const goToRecordsForPoint = (p: {
    label?: string;
    index: number;
    categories: string[];
  }) => {
    // 1) Resolve a raw label safely
    const fromCats =
      typeof p.categories?.[p.index] === "string" ? p.categories[p.index] : "";
    const raw =
      typeof p.label === "string" && p.label.length > 0 ? p.label : fromCats;
    if (!raw) return; // nothing to parse

    // 2) Try multiple formats (strict)
    const m = moment(
      raw,
      ["jYYYY/jMM/jDD", "jYYYY/jM/jD", "YYYY-MM-DD", "YYYY-M-D"],
      true
    );
    if (!m.isValid()) return;

    // Let Apex finish its click/tooltip handlers before unmounting the chart.
    const apply = () => {
      setFromDate(m.clone());
      setTillDate(m.clone());
      setSearchParams({ tab: "2" });
    };
    // Defer to the next frame (or next macrotask as a fallback)
    if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
      requestAnimationFrame(() => setTimeout(apply, 0));
    } else {
      setTimeout(apply, 0);
    }
  };

  // ==== DAILY ====
  const {
    data: consumptionsDatesData,
    isLoading: consumptionsDatesIsLoading,
    status: consumptionsDatesStatus,
    refetch: consumptionDatesRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getAll/consumption/date",
        {
          page: 1,
          count: 1000,
          project_id:
            deviceInfo[0].water_meter_project_info?.project_id ?? null,
          user_id: null,
          water_meters: deviceSerial,
          type_id: null,
          tag_id: deviceInfo[0].water_meter_tag_info.water_meter_tag_id,
          start_time: fromDate.format("YYYY-M-D"),
          end_time: tillDate.format("YYYY-MM-D"),
        },
        "post"
      ),
    // keep your key pattern
    queryKey: ["consumptionsDates", deviceSerial],
  });

  // ==== CUMULATIVE ====
  const {
    data: cumulativeDatesData,
    isLoading: cumulativeDatesIsLoading,
    status: cumulativeDatesStatus,
    refetch: cumulativeDatesRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        // keep same prefix pattern as your daily endpoint
        "watermeters/admin/getAll/consumption/date/cumulative",
        {
          page: 1,
          count: 1000,
          project_id:
            deviceInfo[0].water_meter_project_info?.project_id ?? null,
          user_id: null,
          water_meters: deviceSerial, // ONE meter expected
          type_id: null,
          tag_id: deviceInfo[0].water_meter_tag_info.water_meter_tag_id,
          start_time: fromDate.format("YYYY-M-D"),
          end_time: tillDate.format("YYYY-MM-D"),
        },
        "post"
      ),
    queryKey: ["consumptionsDatesCumulative", deviceSerial],
    // Optional: only fetch when tab is active
    enabled: chartType === "cumulative",
  });

  // Helper: build chartData/chartOptions for cumulative to feed MixChart

  const buildCumulativeChartPayload = () => {
    type Row = {
      date: string;
      cumulative_value: number | string | null | undefined;
    };

    // 1) Normalize and sort
    const rows: Row[] = (cumulativeDatesData?.data ?? []) as Row[];
    const sorted = [...rows].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // 2) Convert Gregorian → Jalali for labels
    const categories: string[] = sorted.map((r) =>
      moment(r.date, "YYYY-MM-DD").format("jYYYY/jMM/jDD")
    );

    // 3) Numeric values
    const data: number[] = sorted.map((r) => Number(r.cumulative_value ?? 0));

    // 4) Series for MixChart (same shape)
    const chartData = [
      {
        name: "تجمعی",
        type: "line",
        data,
      },
    ];

    // 5) chartOptions = x-axis categories (Jalali dates)
    const chartOptions = categories;

    // 6) Total = last - first
    const total =
      data.length >= 2 ? data[data.length - 1] - data[0] : data[0] ?? 0;

    return { chartData, chartOptions, total };
  };

  // Pick status/loader per active tab
  const currentIsLoading =
    chartType === "daily"
      ? consumptionsDatesIsLoading
      : chartType === "cumulative"
      ? cumulativeDatesIsLoading
      : false;

  const currentStatus =
    chartType === "daily"
      ? consumptionsDatesStatus
      : chartType === "cumulative"
      ? cumulativeDatesStatus
      : "success";

  const cumulativePayload =
    chartType === "cumulative" ? buildCumulativeChartPayload() : null;

  return (
    <>
      <div className="projects-overview pt-2">
        {mode !== "dashboard" && (
          <div className="mt-2 py-4 !text-sm font-bold text-navy-700 2xl:!text-base 4xl:!text-lg dark:text-white">
            {deviceInfo && deviceInfo.length > 0
              ? `نمودار مصرف ${deviceInfo[0].water_meter_name}`
              : "دستگاه پیدا نشد"}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-4 pb-4">
          <button
            className={`rounded-lg px-4 py-2 ${
              chartType === "daily"
                ? isDark
                  ? "bg-brand-400 text-white"
                  : "bg-blue-500 text-white"
                : isDark
                ? "bg-gray-700 text-gray-200"
                : "bg-gray-200 text-gray-800"
            }`}
            onClick={() => setChartType("daily")}
          >
            نمودار مصرف روزانه
          </button>

          {AdminPermissions.includes("Admin") && (
            <>
              <button
                className={`rounded-lg px-4 py-2 ${
                  chartType === "cumulative"
                    ? isDark
                      ? "bg-brand-400 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "bg-gray-700 text-gray-200"
                    : "bg-gray-200 text-gray-800"
                }`}
                onClick={() => setChartType("cumulative")}
              >
                نمودار تجمعی روزانه
              </button>
            </>
          )}

          <button
            className={`rounded-lg px-4 py-2 ${
              chartType === "range"
                ? isDark
                  ? "bg-brand-400 text-white"
                  : "bg-blue-500 text-white"
                : isDark
                ? "bg-gray-700 text-gray-200"
                : "bg-gray-200 text-gray-800"
            }`}
            onClick={() => setChartType("range")}
          >
            نمودار مصرف در بازه
          </button>
        </div>

        {mode !== "dashboard" && (
          <>
            {(chartType === "daily" || chartType === "cumulative") && (
              <div className="chart-statistics py-2">
                <ConsumptionChartFilter
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  tillDate={tillDate}
                  setTillDate={setTillDate}
                  update={
                    chartType === "daily"
                      ? consumptionDatesRefetch
                      : cumulativeDatesRefetch
                  }
                  chartTag={{
                    label:
                      deviceInfo?.[0]?.water_meter_tag_info
                        ?.water_meter_tag_name ?? "نام‌ وجود ندارد",
                    value:
                      deviceInfo?.[0]?.water_meter_tag_info
                        ?.water_meter_tag_id ?? "",
                  }}
                  total={
                    chartType === "daily"
                      ? consumptionsDatesData?.data
                        ? consumptionsDatesData?.data?.total
                        : 0
                      : cumulativePayload?.total ?? 0
                  }
                  tab="chart"
                />
              </div>
            )}

            {/* Chart Types and Start Time for ApexChart */}
            {chartType === "range" && (
              <div className="flex items-center justify-between gap-4 pb-4">
                <div className="flex items-end gap-2">
                  <DateInput
                    label="تاریخ شروع"
                    value={startDate}
                    onChange={() => {}}
                    disabled={true}
                  />

                  <div className="flex items-end gap-2">
                    <DateInput
                      label="تاریخ پایان"
                      value={endDate}
                      onChange={setEndDate}
                    />

                    {/* NEW: yesterday / tomorrow buttons (only change تاریخ پایان) */}
                    <button
                      className={`rounded-lg px-3 py-2 ${
                        isDark
                          ? "bg-gray-700 text-gray-200"
                          : "bg-gray-200 text-gray-800"
                      }`}
                      onClick={() => shiftEndDate(-1)}
                      title="یک روز عقب"
                    >
                      روز قبل
                    </button>
                    <button
                      className={`rounded-lg px-3 py-2 ${
                        isDark
                          ? "bg-gray-700 text-gray-200"
                          : "bg-gray-200 text-gray-800"
                      }`}
                      onClick={() => shiftEndDate(1)}
                      title="یک روز جلو"
                    >
                      روز بعد
                    </button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    className={`rounded-lg px-4 py-2 ${
                      apexChartType === "bar"
                        ? isDark
                          ? "bg-brand-400 text-white"
                          : "bg-blue-500 text-white"
                        : isDark
                        ? "bg-gray-700 text-gray-200"
                        : "bg-gray-200 text-gray-800"
                    }`}
                    onClick={() => setApexChartType("bar")}
                  >
                    bar
                  </button>
                  <button
                    className={`rounded-lg px-4 py-2 ${
                      apexChartType === "line"
                        ? isDark
                          ? "bg-brand-400 text-white"
                          : "bg-blue-500 text-white"
                        : isDark
                        ? "bg-gray-700 text-gray-200"
                        : "bg-gray-200 text-gray-800"
                    }`}
                    onClick={() => setApexChartType("line")}
                  >
                    line
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {!currentIsLoading && currentStatus === "success" ? (
          <div className="chart-container mx-auto h-[500px] w-full min-w-[95%]">
            {chartType === "daily" && (
              <MixChart
                chartData={renderComplexChartData(
                  consumptionsDatesData.data,
                  fromDate,
                  tillDate
                )}
                chartOptions={renderComplexChartOptions(fromDate, tillDate)}
                chartTag={{
                  label:
                    deviceInfo[0].water_meter_tag_info.water_meter_tag_name,
                  value: deviceInfo[0].water_meter_tag_info.water_meter_tag_id,
                }}
                onPointClick={goToRecordsForPoint}
              />
            )}

            {/* cumulative chart */}
            {AdminPermissions.includes("Admin") && (
              <>
                {chartType === "cumulative" && cumulativePayload && (
                  <MixChart
                    key={`cumulative-${deviceSerial}-${fromDate.format(
                      "YYYY-MM-DD"
                    )}-${tillDate.format("YYYY-MM-DD")}`}
                    chartData={cumulativePayload.chartData}
                    chartOptions={cumulativePayload.chartOptions}
                    chartTag={{
                      label:
                        deviceInfo[0].water_meter_tag_info.water_meter_tag_name,
                      value:
                        deviceInfo[0].water_meter_tag_info.water_meter_tag_id,
                    }}
                    onPointClick={goToRecordsForPoint}
                  />
                )}
              </>
            )}

            {chartType === "range" && (
              <ApexChart
                key={`${apexChartType}-${endDate.format("YYYY-MM-DD")}`}
                project_id={
                  deviceInfo[0].water_meter_project_info?.project_id ?? null
                }
                user_id={null}
                water_meters={deviceSerial!}
                type_id={null}
                chart_type={apexChartType}
                tag_id={deviceInfo[0].water_meter_tag_info.water_meter_tag_id}
                endDate={endDate}
                setStartDate={setStartDate}
                apexSelection={apexSelection}
                setApexSelection={setApexSelection}
                mode={mode}
              />
            )}
          </div>
        ) : (
          <div>chart is loading</div>
        )}
      </div>
    </>
  );
};

export default CounterConsumptionChart;
